/* ============================================================================
   ACEPLORE · module-ui.js — header, nav, mobile menu, theme toggle, announcement
   rotation, accordions, tabs, localization, generic disclosure behaviors.
   ========================================================================== */
(function () {
  'use strict';
  const { utils, events } = window.Lumina;

  /* ---- Sticky header shadow + transparent->solid on scroll -------------- */
  class LuminaHeader extends window.Lumina.Element {
    connectedCallback() {
      this.setVar();
      this.onScroll = utils.throttle(() => {
        const y = window.scrollY;
        this.classList.toggle('is-scrolled', y > 8);
      }, 100);
      this.listen(window, 'scroll', this.onScroll, { passive: true });
      this.onScroll();
      this.canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      // Desktop nav dropdowns (hover + keyboard/click)
      this.querySelectorAll('[data-nav-item]').forEach((item) => this.bindNavItem(item));
      this.listen(document, 'keydown', (e) => { if (e.key === 'Escape') this.closeMenusAndRestoreFocus(); });
      this.bindMegaCat();
    }
    bindMegaCat() {
      // Taxonomy mega-menus (Shop by Category / Profession / Goal / Format).
      // Each trigger carries data-megacat-trigger="<id>" pairing it with the
      // panel [data-megacat="<id>"]; a value-less trigger (legacy markup)
      // falls back to the first panel.
      this.megas = [];
      this.querySelectorAll('[data-megacat-item]').forEach((item) => {
        const trigger = item.querySelector('[data-megacat-trigger]');
        if (!trigger) return;
        const id = trigger.getAttribute('data-megacat-trigger');
        const mega = id ? this.querySelector(`[data-megacat="${id}"]`) : this.querySelector('[data-megacat]');
        if (!mega) return;
        let timer;
        const entry = { item, trigger, mega };
        entry.open = () => {
          clearTimeout(timer);
          this.closeAllNav();
          this.closeAllMega(entry);
          // Give the panel the exact space between the header and the viewport
          // bottom so every department stays visible (rail scrolls only when
          // the screen is genuinely too short).
          const top = mega.getBoundingClientRect().top;
          mega.style.setProperty('--lm-megacat-max', Math.max(320, window.innerHeight - top - 20) + 'px');
          mega.classList.add('is-open');
          mega.setAttribute('aria-hidden', 'false');
          item.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
        };
        entry.close = () => {
          clearTimeout(timer);
          mega.classList.remove('is-open');
          mega.setAttribute('aria-hidden', 'true');
          item.classList.remove('is-open');
          trigger.setAttribute('aria-expanded', 'false');
        };
        this.megas.push(entry);
        if (this.canHover) {
          this.listen(item, 'mouseenter', entry.open);
          this.listen(item, 'mouseleave', () => { timer = setTimeout(entry.close, 140); });
          this.listen(mega, 'mouseenter', () => clearTimeout(timer));
          this.listen(mega, 'mouseleave', () => { timer = setTimeout(entry.close, 140); });
        }
        this.listen(trigger, 'click', (e) => { e.preventDefault(); mega.classList.contains('is-open') ? entry.close() : entry.open(); });
        // Department rail → panel switching (scoped to this menu's panel)
        const depts = Array.from(mega.querySelectorAll('[data-dept]'));
        const panels = Array.from(mega.querySelectorAll('[data-dept-panel]'));
        const deptBtns = Array.from(mega.querySelectorAll('[data-dept-btn]'));
        deptBtns.forEach((btn) => {
          const i = btn.getAttribute('data-dept-btn');
          const activate = () => {
            depts.forEach((d) => d.classList.toggle('is-active', d.getAttribute('data-dept') === i));
            panels.forEach((p) => p.classList.toggle('is-active', p.getAttribute('data-dept-panel') === i));
            // Expose which department's panel is showing to screen readers.
            deptBtns.forEach((b) => b.setAttribute('aria-current', b === btn ? 'true' : 'false'));
          };
          this.listen(btn, 'mouseenter', activate);
          // Keyboard path: activating on bare focus made every panel except the
          // last unreachable (all rail buttons precede the panels, so tabbing
          // onward kept switching panels away). Enter/Space activates and jumps
          // focus into the chosen panel instead.
          this.listen(btn, 'click', () => {
            activate();
            const panel = panels.find((p) => p.getAttribute('data-dept-panel') === i);
            const firstLink = panel && panel.querySelector('a, button');
            if (firstLink) firstLink.focus();
          });
        });
      });
      if (!this.megas.length) return;
      this.listen(document, 'click', (e) => {
        this.megas.forEach((m) => { if (!m.item.contains(e.target) && !m.mega.contains(e.target)) m.close(); });
      });
    }
    closeAllMega(except) {
      (this.megas || []).forEach((m) => { if (m !== except) m.close(); });
    }
    closeMenusAndRestoreFocus() {
      const active = document.activeElement;
      let trigger = null;
      const mega = (this.megas || []).find((m) => m.item.classList.contains('is-open') && (m.item.contains(active) || m.mega.contains(active)));
      if (mega) trigger = mega.trigger;
      if (!trigger) {
        const nav = Array.from(this.querySelectorAll('[data-nav-item].is-open')).find((item) => item.contains(active));
        if (nav) trigger = nav.querySelector('[data-nav-trigger]');
      }
      this.closeAllNav();
      this.closeAllMega();
      if (trigger) trigger.focus({ preventScroll: true });
    }
    setVar() {
      const h = this.offsetHeight;
      if (h) document.documentElement.style.setProperty('--lm-header-height', h + 'px');
    }
    bindNavItem(item) {
      const trigger = item.querySelector('[data-nav-trigger]');
      if (!trigger) return;
      let timer;
      const open = () => { clearTimeout(timer); this.closeAllNav(item); this.closeAllMega(); item.classList.add('is-open'); trigger.setAttribute('aria-expanded', 'true'); };
      const close = () => { item.classList.remove('is-open'); trigger.setAttribute('aria-expanded', 'false'); };
      if (this.canHover) {
        this.listen(item, 'mouseenter', open);
        this.listen(item, 'mouseleave', () => { timer = setTimeout(close, 120); });
      }
      this.listen(trigger, 'click', (e) => {
        // First touch/keyboard activation opens the submenu. Activating the
        // already-open parent follows the link's real destination.
        if (!item.classList.contains('is-open')) { e.preventDefault(); open(); }
      });
      this.listen(item, 'focusout', (e) => { if (!item.contains(e.relatedTarget)) close(); });
    }
    closeAllNav(except) {
      this.querySelectorAll('[data-nav-item].is-open').forEach((i) => {
        if (i === except) return;
        i.classList.remove('is-open');
        i.querySelector('[data-nav-trigger]')?.setAttribute('aria-expanded', 'false');
      });
    }
  }
  if (!customElements.get('lumina-header')) customElements.define('lumina-header', LuminaHeader);

  /* ---- Mobile menu drawer ----------------------------------------------- */
  (function mobileMenu() {
    const drawer = document.getElementById('MobileMenu');
    if (!drawer) return;
    const overlay = document.querySelector('[data-menu-overlay]');
    let release = null;
    let isOpen = false;
    const open = () => {
      if (isOpen) return;
      isOpen = true;
      utils.lockScroll(true);
      drawer.removeAttribute('inert');
      drawer.setAttribute('aria-hidden', 'false');
      overlay && overlay.setAttribute('aria-hidden', 'false');
      document.querySelector('[data-menu-open]')?.setAttribute('aria-expanded', 'true');
      release = utils.trapFocus(drawer, document.querySelector('[data-menu-open]'));
    };
    const close = () => {
      if (!isOpen) return;
      isOpen = false;
      utils.lockScroll(false);
      release && release();
      release = null;
      drawer.setAttribute('aria-hidden', 'true');
      drawer.setAttribute('inert', '');
      overlay && overlay.setAttribute('aria-hidden', 'true');
      document.querySelector('[data-menu-open]')?.setAttribute('aria-expanded', 'false');
    };
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-menu-open]')) { e.preventDefault(); open(); }
      else if (e.target.closest('[data-menu-close]') || e.target === overlay) close();
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && drawer.getAttribute('aria-hidden') === 'false') close(); });
  })();

  /* ---- Theme (dark mode) toggle ----------------------------------------- */
  class ThemeToggle extends window.Lumina.Element {
    connectedCallback() {
      this.btn = this.querySelector('[data-theme-toggle]');
      this.sync();
      this.listen(this.btn, 'click', () => {
        const dark = document.documentElement.getAttribute('data-theme') === 'dark';
        this.set(!dark);
      });
      // React to system changes when in auto mode and no explicit pref.
      if (document.documentElement.dataset.darkMode === 'auto') {
        const mq = matchMedia('(prefers-color-scheme: dark)');
        this.listen(mq, 'change', (e) => { if (utils.storage.get('lumina:theme') == null) this.apply(e.matches); });
      }
    }
    set(dark) { utils.storage.set('lumina:theme', dark ? 'dark' : 'light'); this.apply(dark); }
    apply(dark) {
      document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
      document.querySelectorAll('lumina-theme-toggle').forEach((toggle) => toggle.sync?.());
    }
    sync() {
      const dark = document.documentElement.getAttribute('data-theme') === 'dark';
      this.btn.setAttribute('aria-pressed', String(dark));
    }
  }
  if (!customElements.get('lumina-theme-toggle')) customElements.define('lumina-theme-toggle', ThemeToggle);
  // Map dark scheme class onto <html> when dark is active.
  const darkSchemeObserver = () => {
    const dark = document.documentElement.getAttribute('data-theme') === 'dark';
    const scheme = window.Lumina.config.darkScheme || 'scheme-dark';
    document.body.classList.toggle('color-' + scheme, dark);
  };
  new MutationObserver(darkSchemeObserver).observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
  darkSchemeObserver();

  /* ---- Announcement rotation -------------------------------------------- */
  class Announcement extends window.Lumina.Element {
    connectedCallback() {
      this.track = this.querySelector('[data-ann-track]');
      this.items = this.querySelectorAll('[data-ann-item]');
      if (this.items.length < 2) return;
      this.index = 0;
      this.go(0);
      this.querySelector('[data-ann-next]') && this.listen(this.querySelector('[data-ann-next]'), 'click', () => this.go(this.index + 1));
      this.querySelector('[data-ann-prev]') && this.listen(this.querySelector('[data-ann-prev]'), 'click', () => this.go(this.index - 1));
      this.toggleBtn = this.querySelector('[data-ann-toggle]');
      if (this.dataset.autorotate === 'true' && !utils.prefersReducedMotion()) {
        const speed = parseInt(this.dataset.speed, 10) || 5000;
        this._userPaused = false;
        // start() respects the user's explicit pause — hover/focus may only
        // pause, never restart, once the user pressed the toggle (WCAG 2.2.2).
        const start = () => {
          clearInterval(this.timer);
          if (this._userPaused) return;
          this.timer = setInterval(() => this.go(this.index + 1), speed);
        };
        start();
        this.listen(this, 'mouseenter', () => clearInterval(this.timer));
        // Resume rotation once the pointer leaves so hover only pauses, not stops.
        this.listen(this, 'mouseleave', start);
        // Keyboard users get the same pause affordance as hover.
        this.listen(this, 'focusin', () => clearInterval(this.timer));
        this.listen(this, 'focusout', (e) => { if (!this.contains(e.relatedTarget)) start(); });
        if (this.toggleBtn) {
          const pauseIc = this.toggleBtn.querySelector('.lm-announcement__toggle-pause');
          const playIc = this.toggleBtn.querySelector('.lm-announcement__toggle-play');
          this.listen(this.toggleBtn, 'click', () => {
            this._userPaused = !this._userPaused;
            if (this._userPaused) clearInterval(this.timer); else start();
            this.toggleBtn.setAttribute(
              'aria-label',
              (this._userPaused ? this.toggleBtn.dataset.labelPlay : this.toggleBtn.dataset.labelPause) || ''
            );
            if (pauseIc) pauseIc.hidden = this._userPaused;
            if (playIc) playIc.hidden = !this._userPaused;
          });
        }
      } else if (this.toggleBtn) {
        // Rotation is off (reduced motion) — a play/pause control is meaningless.
        this.toggleBtn.hidden = true;
      }
    }
    go(i) {
      const n = this.items.length;
      this.index = (i + n) % n;
      this.track.style.transform = `translateX(-${this.index * 100}%)`;
      // Off-screen slides must be invisible to AT and untabbable, or Tab lands
      // on links the user cannot see.
      this.items.forEach((item, idx) => {
        const active = idx === this.index;
        item.setAttribute('aria-hidden', String(!active));
        item.querySelectorAll('a, button').forEach((f) => {
          if (active) f.removeAttribute('tabindex');
          else f.setAttribute('tabindex', '-1');
        });
      });
    }
    disconnectedCallback() { clearInterval(this.timer); super.disconnectedCallback(); }
  }
  if (!customElements.get('lumina-announcement')) customElements.define('lumina-announcement', Announcement);

  /* ---- Localization dropdowns ------------------------------------------- */
  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-loc-toggle]');
    if (toggle) {
      const list = toggle.parentElement.querySelector('[data-loc-list]');
      const open = list.hasAttribute('hidden');
      utils.qsa('[data-loc-list]').forEach((l) => l.setAttribute('hidden', ''));
      utils.qsa('[data-loc-toggle]').forEach((t) => t.setAttribute('aria-expanded', 'false'));
      if (open) { list.removeAttribute('hidden'); toggle.setAttribute('aria-expanded', 'true'); }
      return;
    }
    if (!e.target.closest('.lm-loc')) {
      utils.qsa('[data-loc-list]').forEach((l) => l.setAttribute('hidden', ''));
      utils.qsa('[data-loc-toggle]').forEach((t) => t.setAttribute('aria-expanded', 'false'));
    }
  });

  /* ---- Accordions (data-accordion) — document-delegated for resilience ----
     One delegated listener instead of per-element binding: accordions then work
     no matter when their markup arrives (first paint, theme-editor section
     reload, AJAX) and can never be missed by an init-timing race or a hiccup
     elsewhere on the page — the cause of "FAQ won't expand" on a full page. */
  function setAccordionOpen(trigger, open) {
    const panel = document.getElementById(trigger.getAttribute('aria-controls'));
    trigger.setAttribute('aria-expanded', String(open));
    if (!panel) return;
    panel.setAttribute('aria-hidden', String(!open));
    panel.toggleAttribute('inert', !open);
    panel.style.height = open ? panel.scrollHeight + 'px' : '0px';
  }
  function initAccordions(root = document) {
    utils.qsa('[data-accordion-trigger]', root).forEach((trigger) => {
      setAccordionOpen(trigger, trigger.getAttribute('aria-expanded') === 'true');
    });
  }
  initAccordions();
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-accordion-trigger]');
    if (!trigger) return;
    const acc = trigger.closest('[data-accordion]');
    const isOpen = trigger.getAttribute('aria-expanded') === 'true';
    if (acc && acc.dataset.accordion === 'single' && !isOpen) {
      acc.querySelectorAll('[data-accordion-trigger][aria-expanded="true"]').forEach((t) => setAccordionOpen(t, false));
    }
    setAccordionOpen(trigger, !isOpen);
  });
  // Keep an open panel's height correct when the viewport (and thus content) reflows.
  window.addEventListener('resize', utils.debounce(() => {
    utils.qsa('[data-accordion-trigger][aria-expanded="true"]').forEach((t) => {
      const p = document.getElementById(t.getAttribute('aria-controls'));
      if (p) p.style.height = p.scrollHeight + 'px';
    });
  }, 200));
  events.on('section:load', (d) => { initAccordions(d.target); initTabs(d.target); });

  /* ---- Tabs (data-tabs) ------------------------------------------------- */
  function initTabs(root = document) {
    utils.qsa('[data-tabs]', root).forEach((tabs) => {
      if (tabs.dataset.bound) return;
      tabs.dataset.bound = '1';
      const triggers = utils.qsa('[role="tab"]', tabs);
      triggers.forEach((tab, i) => {
        tab.addEventListener('click', () => activate(tabs, triggers, i));
        tab.addEventListener('keydown', (e) => {
          let target = null;
          if (e.key === 'ArrowRight') target = triggers[(i + 1) % triggers.length];
          if (e.key === 'ArrowLeft') target = triggers[(i - 1 + triggers.length) % triggers.length];
          if (target) { e.preventDefault(); target.focus(); target.click(); }
        });
      });
    });
  }
  function activate(tabs, triggers, idx) {
    triggers.forEach((t, i) => {
      const selected = i === idx;
      t.setAttribute('aria-selected', String(selected));
      t.tabIndex = selected ? 0 : -1;
      const panel = document.getElementById(t.getAttribute('aria-controls'));
      if (panel) panel.hidden = !selected;
    });
  }
  initTabs();

  /* ---- Generic carousel (scroll-snap + arrows) -------------------------- */
  function initCarousels(root = document) {
    utils.qsa('[data-carousel]', root).forEach((car) => {
      if (car.dataset.bound) return;
      car.dataset.bound = '1';
      const track = car.querySelector('[data-carousel-track]');
      const prev = car.querySelector('[data-carousel-prev]');
      const next = car.querySelector('[data-carousel-next]');
      if (!track) return;
      const scrollBy = () => Math.max(track.clientWidth * 0.8, 240);
      prev && prev.addEventListener('click', () => track.scrollBy({ left: -scrollBy(), behavior: utils.prefersReducedMotion() ? 'auto' : 'smooth' }));
      next && next.addEventListener('click', () => track.scrollBy({ left: scrollBy(), behavior: utils.prefersReducedMotion() ? 'auto' : 'smooth' }));
      const update = utils.throttle(() => {
        if (prev) prev.disabled = track.scrollLeft <= 4;
        if (next) next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
      }, 120);
      track.addEventListener('scroll', update, { passive: true });
      update();
    });
  }
  initCarousels();
  events.on('section:load', (d) => initCarousels(d.target));
})();
